import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  RefreshCw, Search, CheckCircle, Clock,
  XCircle, ArrowDownLeft, Smartphone, Bitcoin,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Layout from '../components/Layout';

const P  = '#C29500';
const P2 = '#E8C040';
const G  = '#E8C040';
const R  = '#EF4444';
const C  = '#06B6D4';

const CARD = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 12,
};

const STATUS_CFG = {
  completed  : { color: G,         icon: <CheckCircle size={11}/>, label: 'Completed'  },
  processing : { color: C,         icon: <Clock size={11}/>,       label: 'Processing' },
  pending    : { color: '#F59E0B', icon: <Clock size={11}/>,       label: 'Pending'    },
  failed     : { color: R,         icon: <XCircle size={11}/>,     label: 'Failed'     },
};

// Normalize "USD" stored in DB to display label "USDT"
const normCurrency = (c) => (c === 'USD' ? 'USDT' : c || 'USDT');

const CURRENCY_COLOR = { USDT: C, GHS: G, NGN: P2 };
const currColor = (c) => CURRENCY_COLOR[normCurrency(c)] || '#64748B';

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 9px', borderRadius: 6, fontSize: 11, fontWeight: 600,
      color: cfg.color, background: `${cfg.color}14`, border: `1px solid ${cfg.color}30`,
    }}>
      {cfg.icon}{cfg.label}
    </span>
  );
}

function CurrencyBadge({ currency }) {
  const label = normCurrency(currency);
  const color = currColor(currency);
  return (
    <span style={{
      fontSize: 11, fontWeight: 700,
      color, background: `${color}14`, border: `1px solid ${color}30`,
      padding: '2px 8px', borderRadius: 4,
    }}>
      {label}
    </span>
  );
}

function DepositIcon({ currency }) {
  const color = currColor(currency);
  const norm  = normCurrency(currency);
  const icon  = norm === 'GHS'
    ? <Smartphone size={16} style={{ color }}/>
    : <Bitcoin size={16} style={{ color }}/>;
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 10, flexShrink: 0,
      background: `${color}14`, border: `1px solid ${color}20`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </div>
  );
}

const PAGE_SIZE = 50;
const FILTERS    = ['all', 'completed', 'processing', 'pending', 'failed'];
const CURRENCIES = ['all', 'USDT', 'GHS', 'NGN'];

export default function CollectionHistory() {
  const [deposits,    setDeposits]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore,     setHasMore]     = useState(false);
  const [search,      setSearch]      = useState('');
  const [filter,      setFilter]      = useState('all');
  const [currency,    setCurrency]    = useState('all');

  const fetchPage = useCallback(async (skip = 0, replace = true) => {
    replace ? setLoading(true) : setLoadingMore(true);
    try {
      const res = await api.get('/wallet/transactions', {
        params: { skip, limit: PAGE_SIZE, tx_type: 'deposit' },
      });
      const page = (res.data || []);
      setHasMore(page.length === PAGE_SIZE);
      setDeposits(prev => replace ? page : [...prev, ...page]);
    } catch {
      toast.error('Failed to load collections');
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }, []);

  useEffect(() => { fetchPage(0, true); }, [fetchPage]);

  const loadMore = () => fetchPage(deposits.length, false);

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  const filtered = deposits.filter(d => {
    const norm          = normCurrency(d.currency);
    const matchSearch   = (d.reference   || '').toLowerCase().includes(search.toLowerCase()) ||
                          (d.description || '').toLowerCase().includes(search.toLowerCase());
    const matchStatus   = filter === 'all'   || d.status === filter;
    const matchCurrency = currency === 'all' || norm     === currency;
    return matchSearch && matchStatus && matchCurrency;
  });

  const totalUsdt    = deposits.filter(d => d.status === 'completed').reduce((s, d) => s + (d.amount_usdt || 0), 0);
  const pendingCount = deposits.filter(d => ['pending', 'processing'].includes(d.status)).length;

  if (loading) return (
    <Layout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, border: `3px solid ${G}`,
            borderTopColor: 'transparent', borderRadius: '50%',
            animation: 'spin .7s linear infinite', margin: '0 auto 12px',
          }}/>
          <p style={{ color: '#475569', fontSize: 14 }}>Loading collections…</p>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </Layout>
  );

  return (
    <Layout>
      <div style={{ padding: 28, maxWidth: 1200, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#fff', margin: 0, letterSpacing: -0.5 }}>Collections</h1>
            <p style={{ fontSize: 13, color: '#64748B', margin: '6px 0 0' }}>All incoming deposits across every currency</p>
          </div>
          <motion.button whileHover={{ rotate: 180 }} transition={{ duration: 0.3 }}
            onClick={() => fetchPage(0, true)}
            style={{
              padding: 10, borderRadius: 10, cursor: 'pointer',
              background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
              color: '#64748B', display: 'flex', alignItems: 'center',
            }}>
            <RefreshCw size={16}/>
          </motion.button>
        </motion.div>

        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { label: 'Total Deposits',       value: deposits.length,             color: C         },
            { label: 'Total Collected',       value: `$${totalUsdt.toFixed(2)}`,  color: G         },
            { label: 'Pending / Processing',  value: pendingCount,                color: '#F59E0B' },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              style={{ ...CARD, padding: 20, textAlign: 'center' }}>
              <p style={{ fontSize: 11, color: '#64748B', margin: '0 0 6px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</p>
              <p style={{ fontSize: 24, fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
            </motion.div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569' }}/>
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by reference or description…"
              style={{
                width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 10, padding: '10px 14px 10px 36px', color: '#fff', fontSize: 13,
                outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => e.target.style.borderColor = `${G}50`}
              onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
            />
          </div>

          <div style={{ display: 'flex', gap: 5 }}>
            {CURRENCIES.map(cur => (
              <button key={cur} onClick={() => setCurrency(cur)}
                style={{
                  padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer',
                  background: currency === cur ? `${G}20` : 'rgba(255,255,255,0.04)',
                  border: currency === cur ? `1px solid ${G}40` : '1px solid rgba(255,255,255,0.08)',
                  color: currency === cur ? G : '#64748B',
                }}>
                {cur}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {FILTERS.map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', textTransform: 'capitalize',
                  background: filter === f ? `linear-gradient(135deg,${P},${P2})` : 'rgba(255,255,255,0.04)',
                  border: filter === f ? 'none' : '1px solid rgba(255,255,255,0.08)',
                  color: filter === f ? '#000' : '#64748B',
                }}>
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ ...CARD, overflow: 'hidden' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '56px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: 'rgba(255,255,255,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <ArrowDownLeft size={26} style={{ color: '#334155' }}/>
              </div>
              <p style={{ fontSize: 14, color: '#475569', fontWeight: 600, margin: 0 }}>No deposits found</p>
              <p style={{ fontSize: 12, color: '#334155', margin: 0 }}>Your collection history will appear here</p>
            </div>
          ) : (
            <div>
              {filtered.map((d, i) => {
                const norm      = normCurrency(d.currency);
                const isLocal   = norm === 'GHS' || norm === 'NGN';
                const primary   = isLocal && d.amount_local
                  ? `${norm} ${(d.amount_local).toLocaleString()}`
                  : `$${(d.amount_usdt || 0).toFixed(2)} USDT`;

                return (
                  <motion.div key={d.id || i}
                    initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.04, 0.6) }}
                    style={{
                      padding: '16px 20px',
                      borderBottom: i < filtered.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                    }}
                    onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                    onMouseOut={e => e.currentTarget.style.background = 'transparent'}>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <DepositIcon currency={d.currency}/>

                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{primary}</span>
                          <CurrencyBadge currency={d.currency}/>
                          <StatusBadge status={d.status}/>
                        </div>

                        <p style={{ fontSize: 12, color: '#64748B', margin: '0 0 3px' }}>
                          {d.description || 'Deposit'}
                          {d.network && ` · ${d.network}`}
                          {d.phone   && ` · ${d.phone}`}
                        </p>

                        <p style={{ fontSize: 11, color: '#334155', margin: 0 }}>
                          {d.reference && `Ref: ${d.reference} · `}{formatDate(d.created_at)}
                        </p>
                      </div>

                      {/* USDT equiv for local-currency deposits */}
                      {isLocal && d.amount_usdt > 0 && (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <p style={{ fontSize: 13, fontWeight: 700, color: C, margin: '0 0 3px' }}>
                            ≈ ${(d.amount_usdt).toFixed(2)} USDT
                          </p>
                          {d.rate && (
                            <p style={{ fontSize: 11, color: '#475569', margin: 0 }}>
                              Rate: {d.rate}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {/* Footer: count + load more */}
              <div style={{
                padding: '14px 20px',
                borderTop: '1px solid rgba(255,255,255,0.04)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <p style={{ fontSize: 12, color: '#475569', margin: 0 }}>
                  Showing {filtered.length} of {deposits.length} deposits
                </p>
                {hasMore && (
                  <motion.button
                    whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                    onClick={loadMore} disabled={loadingMore}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                      background: `${P}14`, border: `1px solid ${P}30`,
                      color: P2, fontSize: 12, fontWeight: 600,
                    }}>
                    {loadingMore
                      ? <div style={{ width: 12, height: 12, border: `1.5px solid ${P2}`, borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin .7s linear infinite' }}/>
                      : <RefreshCw size={12}/>
                    }
                    Load More
                  </motion.button>
                )}
              </div>
            </div>
          )}
        </motion.div>

      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #334155; }
      `}</style>
    </Layout>
  );
}
